import { useRef } from 'react';
import type { Nudge, NudgeSource } from './types';
import { compareNudges } from './types';

/**
 * Resolves a list of nudge sources into the ordered, capped list to render.
 *
 * IMPORTANT: `sources` must be a STABLE array — same members, same order, on every render.
 * Each source is a hook, so a changing array breaks the Rules of Hooks. Declare it at module
 * level (see `NUDGE_SOURCES`), never inline or derived from state.
 */
export const useNudges = (sources: NudgeSource[], limit: number): Nudge[] => {
  // A COPY, not the reference: holding the caller's array would let an in-place mutation slip past
  // the comparison below, since both sides would point at the same (already changed) array.
  const initialSources = useRef([...sources]);

  // Identity and order, not just length: swapping or reordering two sources keeps the length and
  // would still hand each hook the state and analytics effects of the one that used to sit in that
  // slot. Fails loudly in dev instead of corrupting hook order silently.
  if (process.env.NODE_ENV !== 'production') {
    const initial = initialSources.current;
    const changed = sources.length !== initial.length || sources.some((source, index) => source !== initial[index]);
    if (changed) {
      throw new Error('useNudges: `sources` must keep the same members, in the same order, across renders. Declare it at module level, never inline or derived from state.');
    }
  }

  // Every source is called on every render and decides its own eligibility, returning null when
  // ineligible. Hook order is therefore stable, which is what the invariant above guarantees.
  // biome-ignore lint/correctness/useHookAtTopLevel: fixed-length array of hooks; guarded above.
  const resolved = sources.map((useSource) => useSource());

  return resolved
    .filter((nudge): nudge is Nudge => nudge !== null)
    .sort(compareNudges)
    .slice(0, limit);
};

export default useNudges;
